import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FileText, Scale, Languages, Shield, Car, Home, Code, Package, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ChapterIndicator from './ChapterIndicator';
import { useI18n } from '@/contexts/I18nContext';
const services = [{
  icon: FileText,
  title: 'Gestoría',
  description: 'Olvídate de colas y papeleos. Tus impuestos, permisos y trámites resueltos sin estrés.',
  href: '/servicios/gestoria-administracion',
  color: 'from-punto to-red-600',
  badge: 'Más vendido',
  tag: 'Impuestos • Permisos • RAV'
}, {
  icon: Scale,
  title: 'Asesoría Legal',
  description: 'Protege tus derechos en un país donde no dominas las leyes. Duerme tranquilo.',
  href: '/servicios/asesoria-legal',
  color: 'from-blue-600 to-blue-700',
  tag: 'Contratos • Herencias • Visados'
}, {
  icon: Languages,
  title: 'Idiomas',
  description: 'Rompe la barrera del idioma. Consigue mejores trabajos y siéntete como en casa.',
  href: '/servicios/cursos-idiomas',
  color: 'from-emerald-500 to-emerald-600',
  tag: 'Alemán • Inglés • Certificados'
}, {
  icon: Shield,
  title: 'Seguros',
  description: 'Ahorra hasta un 35% en primas sin perder cobertura. Tu familia protegida.',
  href: '/servicios/consultoria-seguros',
  color: 'from-purple-500 to-purple-600',
  tag: 'Salud • 3er Pilar • Vida'
}, {
  icon: Car,
  title: 'Alquiler de Coches',
  description: 'Muévete con libertad desde el primer día. Sin complicaciones.',
  href: '/servicios/alquileres-vehiculos',
  color: 'from-amber-500 to-amber-600',
  tag: 'Turismos • Furgonetas • SUV'
}, {
  icon: Home,
  title: 'Alquiler de Viviendas',
  description: 'Encuentra tu hogar sin competir con 200 candidatos. Acceso a pisos exclusivos.',
  href: '/servicios/alquileres-viviendas',
  color: 'from-rose-500 to-rose-600',
  tag: 'Habitaciones • Pisos • WG'
}, {
  icon: Code,
  title: 'Soluciones Digitales',
  description: 'Multiplica tus ventas con una web profesional, CRM e IA que trabajan por ti.',
  href: '/servicios/soluciones-digitales-avanzadas',
  color: 'from-cyan-500 to-cyan-600',
  badge: 'Nuevo',
  tag: 'Web • CRM • IA'
}, {
  icon: Package,
  title: 'Pack Relocation',
  description: 'Llega a Suiza sin miedo. Todo resuelto antes de aterrizar.',
  href: '/servicios/pack-relocation',
  color: 'from-orange-500 to-orange-600',
  badge: 'Ahorra 25%',
  tag: 'Todo incluido'
}];
const CinematicServicesGrid = () => {
  const { t, localizedPath } = useI18n();
  
  const services = [{
    icon: FileText,
    title: t.services.gestoriaShort,
    description: t.services.gestoriaDesc,
    href: '/servicios/gestoria-administracion',
    color: 'from-punto to-red-600',
    badge: t.services.badgeBestSeller,
    tag: t.services.gestoriaTags,
  }, {
    icon: Scale,
    title: t.services.legalShort,
    description: t.services.legalDesc,
    href: '/servicios/asesoria-legal',
    color: 'from-blue-600 to-blue-700',
    tag: t.services.legalTags,
  }, {
    icon: Languages,
    title: t.services.idiomasShort,
    description: t.services.idiomasDesc,
    href: '/servicios/cursos-idiomas',
    color: 'from-emerald-500 to-emerald-600',
    tag: t.services.idiomasTags,
  }, {
    icon: Shield,
    title: t.services.segurosShort,
    description: t.services.segurosDesc,
    href: '/servicios/consultoria-seguros',
    color: 'from-purple-500 to-purple-600',
    tag: t.services.segurosTags,
  }, {
    icon: Car,
    title: t.services.vehiculosShort,
    description: t.services.vehiculosDesc,
    href: '/servicios/alquileres-vehiculos',
    color: 'from-amber-500 to-amber-600',
    tag: t.services.vehiculosTags,
  }, {
    icon: Home,
    title: t.services.viviendasShort,
    description: t.services.viviendasDesc,
    href: '/servicios/alquileres-viviendas',
    color: 'from-rose-500 to-rose-600',
    tag: t.services.viviendasTags,
  }, {
    icon: Code,
    title: t.services.digitalShort,
    description: t.services.digitalDesc,
    href: '/servicios/soluciones-digitales-avanzadas',
    color: 'from-cyan-500 to-cyan-600',
    badge: t.services.badgeNew,
    tag: t.services.digitalTags,
  }, {
    icon: Package,
    title: t.services.relocationShort,
    description: t.services.relocationDesc,
    href: '/servicios/pack-relocation',
    color: 'from-orange-500 to-orange-600',
    badge: t.services.badgeSave,
    tag: t.services.relocationTags,
  }];

  return <section className="relative py-8 md:py-12 bg-gradient-to-b from-gray-50 via-white to-gray-50 dark:from-cinema-dark dark:via-cinema-surface/30 dark:to-cinema-dark overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-0 w-[400px] h-[400px] bg-punto/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-0 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[150px]" />
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Chapter Indicator */}
        <div className="flex justify-center">
          <ChapterIndicator act={t.services.act} subtitle={t.services.actSubtitle} light />
        </div>
        
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-6">
          
          <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-black text-gray-900 dark:text-white mb-1">
            {t.services.resultsTitle}
            <span className="text-punto"> {t.services.resultsTitleAccent}</span>
          </h2>
          <p className="text-gray-500 dark:text-white/50 text-xs md:text-sm max-w-md mx-auto px-4">
            {t.services.resultsSubtitle}
          </p>
        </motion.div>

        {/* Services Grid - 2 cols mobile, 3 cols tablet, 4 cols desktop */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 mb-8">
          {services.map((service, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          y: 0,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: index * 0.05
        }}>
              <Link to={localizedPath(service.href)} className="block h-full group">
                <div className="relative h-full p-3 md:p-4 rounded-xl bg-white dark:bg-white/[0.03] backdrop-blur-sm border border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20 hover:shadow-md transition-all duration-500 overflow-hidden">
                  {/* Hover Gradient */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`} />
                  
                  {/* Badge */}
                  {service.badge && <div className={`absolute -top-0 -right-0 px-2 py-1 rounded-bl-lg rounded-tr-xl bg-gradient-to-r ${service.color} text-white text-[10px] font-bold shadow-lg`}>
                      {service.badge}
                    </div>}
                  
                  {/* Icon */}
                  <div className={`w-10 h-10 md:w-11 md:h-11 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-3 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-md`}>
                    <service.icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-sm md:text-base font-bold text-gray-900 dark:text-white mb-1 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-500 dark:text-white/50 text-[11px] md:text-xs leading-relaxed mb-2 group-hover:text-gray-600 dark:group-hover:text-white/70 transition-colors line-clamp-2">
                    {service.description}
                  </p>
                  
                  {/* Tag */}
                  {service.tag && (
                    <div className="mb-2">
                      <span className="inline-block px-2 py-0.5 text-[9px] md:text-[10px] font-medium rounded-full bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-white/60 border border-gray-200 dark:border-white/10">
                        {service.tag}
                      </span>
                    </div>
                  )}
                  
                  {/* Arrow */}
                  <div className="flex items-center text-gray-400 dark:text-white/30 group-hover:text-punto transition-all">
                    <span className="text-[11px] md:text-xs font-medium">{t.services.explore}</span>
                    <ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            </motion.div>)}
        </div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="text-center">
          <Link to={localizedPath('/servicios')}>
            <Button size="sm" className="px-5 py-2 text-sm font-medium bg-gray-100 dark:bg-white/5 backdrop-blur-sm border border-gray-300 dark:border-white/10 hover:bg-gray-200 dark:hover:bg-white/10 hover:border-punto/50 rounded-lg transition-all group text-accent">
              {t.services.viewAll}
              <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>;
};
export default CinematicServicesGrid;